// src/pages/earningsImport/EarningsImport.js

import React, { useEffect, useState } from "react";
import { db } from "../../firebase/firebase";
import { collection, doc, getDocs, onSnapshot, } from "firebase/firestore";

// Ride-hailing upload
import EarningsUploadSection from "./EarningsUploadSection";
import EarningsMappingSection from "./EarningsMappingSection";
// Manual Trips
import TripImportReview from "./TripImportReview";

import { parseFileToRows, normalizeRows } from "./earningsParser";
import {
  saveAndMergeMappings,
  saveEarningsToFirestore,
} from "./earningsSaver";

// POS Upload
import POSUploadSection from "./pos/POSUploadSection";
import { parsePOSFile } from "./pos/posParser";
import { processPOSTransactions } from "./pos/posExtractor";
import { savePOSTransactions } from "./pos/posSaver";



// NEW — Car Expenses
import CarExpenses from "../driverReports/carSheets/CarExpenses/CarExpenses";


export default function EarningsImport() {
  const [platform, setPlatform] = useState("");
  const [month, setMonth] = useState("");
  const [week, setWeek] = useState("");
  const [company, setCompany] = useState("");     // ⭐ NEW

  const [file, setFile] = useState(null);

  // Ride-hailing
  const [driverList, setDriverList] = useState([]);
  const [existingMappings, setExistingMappings] = useState({});
  const [normalizedRows, setNormalizedRows] = useState([]);
  const [unmappedRows, setUnmappedRows] = useState([]);
  const [mappingUpdates, setMappingUpdates] = useState({});
  const [autoSaved, setAutoSaved] = useState(false);
  const [hasUnmapped, setHasUnmapped] = useState(false);


  // POS
  const [posFile, setPosFile] = useState(null);
  const [posResults, setPosResults] = useState([]);
   // ============================
  // ============================
// ============================
// NEW – CAR EXPENSES SECTION
// ============================
const [carSheets, setCarSheets] = useState([]);
const [selectedSheetId, setSelectedSheetId] = useState("");
const [showExpensesPanel, setShowExpensesPanel] = useState(false);
const [activeCarSheet, setActiveCarSheet] = useState(null);

function allDriversMapped(rows, mappings) {
  if (!rows || rows.length === 0) return false;

  return rows.every((r) => {
    const platformId = r.driver_id;
    return platformId && mappings[platformId]; // Must exist in Firestore mapping
  });
}

// LOAD FIRESTORE SHEETS ONLY — NO AUTO BUILD
useEffect(() => {
  if (!month) {
    setCarSheets([]);
    setSelectedSheetId("");
    setShowExpensesPanel(false);
    setActiveCarSheet(null);
    return;
  }

  async function loadFirestoreSheets() {
    try {
      console.log("📥 Loading Firestore sheets for month:", month);

      const snap = await getDocs(
        collection(db, "carSheets", month, "sheets")
      );

      const fsSheets = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      console.log("📄 Firestore Sheets Loaded:", fsSheets.length);

      setCarSheets(fsSheets);
      setSelectedSheetId("");
      setShowExpensesPanel(false);
      setActiveCarSheet(null);
    } catch (err) {
      console.error("❌ Error loading Firestore car sheets:", err);
      setCarSheets([]);
    }
  }

  loadFirestoreSheets();
}, [month]);

// Open CarExpenses Panel
function handleOpenExpenses() {
  if (!month) return alert("Select month first.");
  if (!selectedSheetId) return alert("Select a car sheet (car + period).");

  const sheet = carSheets.find((s) => s.id === selectedSheetId);
  if (!sheet) return alert("No sheet found for this selection.");

  setActiveCarSheet(sheet);
  setShowExpensesPanel(true);
}
// ============================
// END NEW SECTION
// ============================


  /* --------------------------------------------------
     Load drivers
  -------------------------------------------------- */
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "drivers"), (snap) => {
      setDriverList(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  /* --------------------------------------------------
     Load platform → driver mappings
  -------------------------------------------------- */
  useEffect(() => {
    if (!platform) return;

    const ref = doc(db, "driverPlatformMappings", platform.toLowerCase());
    const unsub = onSnapshot(ref, (snap) => {
      setExistingMappings(snap.data() || {});
    });

    return () => unsub();
  }, [platform]);

  // ---------------------------------------------------------
// AUTO-SAVE WHEN ALL DRIVERS ARE MAPPED
// ---------------------------------------------------------
useEffect(() => {
  if (!normalizedRows.length) return;
  if (!existingMappings) return;

  const ready = allDriversMapped(normalizedRows, existingMappings);

  // Auto-save should NOT run if user still needs to map drivers
if (ready && !autoSaved && !hasUnmapped) {

    console.log("🔥 AUTO-SAVE TRIGGERED — All drivers mapped.");

    setAutoSaved(true);

    saveEarningsToFirestore({
      rows: normalizedRows,
      platform,
      company,
      month,
      week,
      mergedMappings: existingMappings,
    })
      .then(() => {
        console.log("✅ Auto-save completed successfully.");
        alert("Auto-Save: Earnings File Saved Automatically!");
      })
      .catch((err) => {
        console.error("❌ Auto-save failed", err);
        setAutoSaved(false);
      });
  }

// eslint-disable-next-line react-hooks/exhaustive-deps
}, [normalizedRows, existingMappings]);


  /* --------------------------------------------------
     Process Ride-hailing File
  -------------------------------------------------- */
  async function handleProcessFile() {
    if (!file) return alert("Select a file.");
    if (!platform) return alert("Select platform.");
    if (!company) return alert("Select company.");
    if (!month || !week) return alert("Select month & week.");

    try {
      const extracted = await parseFileToRows(file);
      const norm = normalizeRows(extracted);

      setNormalizedRows(norm);

      // rows without mapping
      const missing = norm
        .filter((r) => !existingMappings[r.driver_id])
        .map((r) => ({ ...r, platform }));

      setUnmappedRows(missing);
setHasUnmapped(missing.length > 0);

    } catch (err) {
      console.error(err);
      alert("Error processing ride-hailing file.");
    }
  }


  /* --------------------------------------------------
     Save platform mapping + earnings
     (MULTI-UPLOAD SUPPORT ADDED)
  -------------------------------------------------- */
  async function handleSaveMapping() {
    if (!company) return alert("Please select a company.");

    const merged = await saveAndMergeMappings(
      mappingUpdates,
      existingMappings,
      platform
    );

    await saveEarningsToFirestore({
      rows: normalizedRows,
      platform,
      company,           // ⭐ VERY IMPORTANT
      month,
      week,
      mergedMappings: merged,
    });

    alert("Ride-hailing earnings imported!");

    setMappingUpdates({});
    setUnmappedRows([]);
  }

  /* --------------------------------------------------
   POS Processing
-------------------------------------------------- */
async function handleProcessPOS() {
  if (!posFile) {
    alert("Upload POS file first.");
    return { ok: false, savedCount: 0, unmatchedCount: 0, errors: ["Missing POS file"] };
  }
  if (!month) {
    alert("Select month first.");
    return { ok: false, savedCount: 0, unmatchedCount: 0, errors: ["Missing month"] };
  }

  console.log("🔥 handleProcessPOS() ENTERED");
  console.log("📦 posFile:", posFile);
  console.log("📅 month:", month);

  try {
    // 1) Parse raw rows from Excel
    const rows = await parsePOSFile(posFile);
    console.log("📄 Parsed POS rows count:", rows.length);

    // 2) Process + attach vehicle info
    // IMPORTANT: processPOSTransactions now returns { rows, unmatched }
    const { rows: processedRows, unmatched } = await processPOSTransactions(rows, month);
    console.log("🚚 Processed POS rows count:", processedRows.length);
    console.log("⚠️ Unmatched rows count:", unmatched.length);

    // 3) SAVE TO FIRESTORE (returns summary object from your updated posSaver.js)
    const result = await savePOSTransactions(month, processedRows, posFile);
    console.log("💾 savePOSTransactions result:", result);

    // 4) Keep your existing behavior (optional)
    setPosResults(processedRows);

    // Alerts (optional)
    if (result?.savedCount > 0) {
      alert(`POS Saved: ${result.savedCount}\nUnmatched: ${result.unmatchedCount || 0}`);
    } else {
      alert(`No transactions saved.\nUnmatched: ${result?.unmatchedCount || 0}`);
    }

    // ✅ Return summary for POSUploadSection UI
    return {
      ok: result?.ok ?? (result?.savedCount > 0),
      savedCount: result?.savedCount ?? 0,
      duplicateCount: result?.duplicateCount ?? 0,
      skippedCount: result?.skippedCount ?? 0,
      unmatchedCount: result?.unmatchedCount ?? unmatched.length,
      unmatched: result?.unmatched ?? unmatched,
      errors: result?.errors ?? [],
      uploadId: result?.uploadId,
    };
  } catch (err) {
    console.error("❌ POS processing failed:", err);
    alert(`POS processing failed: ${err?.message || err}`);

    return {
      ok: false,
      savedCount: 0,
      unmatchedCount: 0,
      unmatched: [],
      errors: [err?.message || String(err)],
    };
  }
}

  /* --------------------------------------------------
     Render
  -------------------------------------------------- */
  return (
    <div style={{ padding: 20 }}>
      <h2>💰 Earnings Import</h2>

      {/* ---------------------- */}
      {/* SELECT COMPANY */}
      {/* ---------------------- */}
      <div style={{ marginBottom: 20 }}>
        <label style={{ fontWeight: "bold" }}>Select Company: </label>
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          style={{ padding: 8, marginLeft: 10 }}
        >
          <option value="">-- Select --</option>
          <option value="Comfort Line Luxury Motor Vehicles Services LLC">
            Comfort Line
          </option>
          <option value="Lolia Limousine for Luxury Motor Vehicles Services LLC">
            Lolia Limousine
          </option>
        </select>
      </div>

      {/* RIDE-HAILING UPLOAD */}
      <EarningsUploadSection
  platform={platform}
  setPlatform={setPlatform}
  company={company}
  setCompany={setCompany}
  month={month}
  setMonth={setMonth}
  week={week}
  setWeek={setWeek}
  onFileSelect={(e) => setFile(e.target.files[0])}
  onProcess={handleProcessFile}
/>



      {/* DRIVER MAPPING */}
      <EarningsMappingSection
        unmappedRows={unmappedRows}
        driverList={driverList}
        mappingUpdates={mappingUpdates}
        setMappingUpdates={setMappingUpdates}
        onSaveMapping={handleSaveMapping}
      />

      <POSUploadSection
  month={month}
  setMonth={setMonth}
  onFileSelect={(f) => setPosFile(f)}
  onProcess={handleProcessPOS}
  results={posResults}
/>


      
{/* MANUAL TRIPS */}
      <h3 style={{ marginTop: 40, marginBottom: 10 }}>
        📥 Manual Trips Import (KOI / Rayna / City Guide)
      </h3>

      <TripImportReview />
      {/* ============================================== */}
{/*        NEW — MONTHLY CAR EXPENSES SECTION       */}
{/* ============================================== */}

<div
  style={{
    marginTop: 50,
    padding: 20,
    background: "#fff8e1",
    border: "1px solid #ffca28",
    borderRadius: 8,
  }}
>
  <h3>🧾 Manage Monthly Car Expenses</h3>

  {/* Month (shared with other sections) */}
<label style={{ fontWeight: "600" }}>Select Month:</label>
<input
  type="month"
  value={month}
  onChange={(e) => setMonth(e.target.value)}
  style={{
    display: "block",
    marginBottom: 12,
    padding: 8,
    width: 220,
  }}
/>

{/* Car Sheet Dropdown (direct from built sheets) */}
<label style={{ fontWeight: "600" }}>Select Car Sheet (Car + Period):</label>
<select
  value={selectedSheetId}
  onChange={(e) => setSelectedSheetId(e.target.value)}
  style={{
    display: "block",
    marginBottom: 12,
    padding: 8,
    width: 360,
  }}
>
  <option value="">-- Select Car Sheet --</option>

  {carSheets.map((sheet) => (
    <option key={sheet.id} value={sheet.id}>
      {sheet.carNumber} — {sheet.carModel || ""} —{" "}
      {sheet.periodLabel || sheet.monthLabel}
    </option>
  ))}
</select>

<button
  onClick={handleOpenExpenses}
  style={{
    padding: "10px 16px",
    background: "#1976d2",
    color: "white",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
    fontWeight: 600,
  }}
>
  Manage Expenses
</button>

{/* Panel */}
{showExpensesPanel && activeCarSheet && (
  <div
    style={{
      marginTop: 20,
      padding: 20,
      border: "1px solid #ccc",
      borderRadius: 8,
      background: "#fff",
    }}
  >
    <CarExpenses
      sheet={activeCarSheet}
      month={month}                 
      onClose={() => setShowExpensesPanel(false)}
      
    />
  </div>
)}

</div>

    </div>
  );
}